import { ApolloError } from "@apollo/client";
import React, {
    ReactElement, useContext, useEffect, useMemo,
} from "react";
import { Loading } from "../components";
import { ErrorDefault } from "../pages/error/Error";
import {
    Permission, useGetCurrentPortalLazyQuery, useGetFeatureFlagsQuery,
} from "../__generated__/api";
import useLogging from "./logging/LoggingContext";

const REFRESH_INTERVAL = 1000 * 60 * 60;

const KEY = "portal";

export interface Portal {
    id?: string;
    title?: string;
    welcomeMessage?: string;
    chatToken?: string;
    lastUpdated: Date;
    indexArticleId?: string;
    permissions: Set<Permission>;
    invalidate: () => void;
    clearCache: () => void;
    featureFlags: Set<string>;
    isAIEnabled: boolean;
}

interface PortalSerialized {
    title?: string;
    id?: string;
    welcomeMessage?: string;
    chatToken?: string;
    indexArticleId?: string;
    lastUpdated: number;
    permissions: Permission[];
    featureFlags: string[];
    isAIEnabled: boolean;
}

export const PortalContext = React.createContext<Portal>({
    lastUpdated: new Date(),
    permissions: new Set(),
    invalidate: () => { /* do nothing */ },
    clearCache: () => { /* do nothing */ },
    featureFlags: new Set(),
    isAIEnabled: false,
});

export function PortalProvider({ children }: {children: ReactElement}): (ReactElement | null) {
    const [getPortal, {
        loading, data, error, refetch,
    }] = useGetCurrentPortalLazyQuery({
        fetchPolicy: "network-only",
    });

    const logging = useLogging();

    const { data: featureFlags } = useGetFeatureFlagsQuery();

    const clearCache = () => {
        localStorage.removeItem(KEY);
    };

    const invalidate = () => {
        clearCache();
        if (refetch) {
            refetch();
        } else {
            getPortal();
        }
    };

    const cachedPortal = localStorage.getItem(KEY);

    const portal: Portal | undefined = useMemo(() => {
        let current: Portal | undefined;
        if (data?.portal) {
            current = {
                id: data.portal.id,
                chatToken: data.portal.chatToken as string,
                title: data.portal.title as string,
                welcomeMessage: data.portal.welcomeMessage as string,
                permissions: new Set(data.portal.permissions),
                lastUpdated: new Date(),
                indexArticleId: data.portal.indexArticleId,
                invalidate,
                clearCache,
                featureFlags: new Set(featureFlags?.featureFlags?.active ?? []),
                isAIEnabled: data.portal.isAIEnabled as boolean,
            };
        }

        if (current) {
            localStorage.setItem(KEY, JSON.stringify({
                ...current,
                lastUpdated: current.lastUpdated.getTime(),
                permissions: Array.from(current.permissions),
                featureFlags: Array.from(current.featureFlags),
            }));
        } else if (cachedPortal) {
            const localPortal = JSON.parse(cachedPortal) as PortalSerialized;
            if (Date.now() - (localPortal.lastUpdated ?? 0) < REFRESH_INTERVAL) {
                current = {
                    id: localPortal.id,
                    lastUpdated: new Date(localPortal.lastUpdated),
                    permissions: new Set(localPortal.permissions),
                    chatToken: localPortal.chatToken,
                    title: localPortal.title,
                    welcomeMessage: localPortal.welcomeMessage,
                    indexArticleId: localPortal.indexArticleId,
                    invalidate,
                    clearCache,
                    isAIEnabled: localPortal.isAIEnabled,
                    featureFlags: new Set(featureFlags?.featureFlags?.active ?? localPortal.featureFlags),
                };
            }
        }

        return current;
    }, [data?.portal, cachedPortal != null, featureFlags?.featureFlags?.active]);

    useEffect(() => {
        if (!portal) {
            getPortal();
        }
    }, []);

    useEffect(() => {
        if (portal) {
            logging.info({
                portalId: portal.id,
                permissions: Array.from(portal.permissions),
                featureFlags: Array.from(portal.featureFlags),
            }, "Portal Started");
        }
    }, [portal?.id]);

    if (error) {
        return <ErrorDefault message={error instanceof ApolloError ? error.message : "Unknown error occured."} />;
    }
    if (loading) {
        return <Loading />;
    }

    if (!portal) {
        return <Loading />;
    }

    return (
        <PortalContext.Provider value={portal}>
            {children}
        </PortalContext.Provider>
    );
}

export default function usePortal(): Portal {
    return useContext(PortalContext) ?? {};
}
